import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@heroui/react';
import { countParam, renderNotice } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { UploadBlock } from '../../uploadState';

type UploadBlockAlertProps = {
    block: UploadBlock;
    /** For a check that did not answer. */
    onRetry: () => void;
};

/** The title and description of every block but the server's notices. */
const useBlockCopy = (
    block: Exclude<UploadBlock, { kind: 'notices' }>,
): { title: string; description: string } => {
    const { t } = useTranslation('pages');
    switch (block.kind) {
        case 'tooManyRows': {
            const limit = countParam(block.limit);
            return {
                title: t('catalogImport.upload.blocked.tooManyRows.title', {
                    rows: countParam(block.rowCount),
                }),
                description: t(
                    'catalogImport.upload.blocked.tooManyRows.description',
                    { limit },
                ),
            };
        }
        case 'missingHeaders':
            return {
                title: t('catalogImport.upload.blocked.missingHeaders.title'),
                description: t(
                    'catalogImport.upload.blocked.missingHeaders.description',
                    { headers: block.headers.join(', ') },
                ),
            };
        case 'noProductsSheet':
            return {
                title: t('catalogImport.upload.blocked.noProductsSheet.title'),
                description: t(
                    'catalogImport.upload.blocked.noProductsSheet.description',
                ),
            };
        case 'unreadable':
            return {
                title: t('catalogImport.upload.blocked.unreadable.title'),
                description: t(
                    'catalogImport.upload.blocked.unreadable.description',
                ),
            };
        case 'empty':
            return {
                title: t('catalogImport.upload.blocked.empty.title'),
                description: t(
                    'catalogImport.upload.blocked.empty.description',
                ),
            };
        case 'checkFailed':
            return {
                title: t('catalogImport.upload.blocked.checkFailed.title'),
                description: t(
                    'catalogImport.upload.blocked.checkFailed.description',
                ),
            };
    }
};

const BlockCopy: React.FC<{
    block: Exclude<UploadBlock, { kind: 'notices' }>;
}> = ({ block }) => {
    const { title, description } = useBlockCopy(block);
    return (
        <>
            <Alert.Title>{title}</Alert.Title>
            <Alert.Description>{description}</Alert.Description>
        </>
    );
};

/**
 * Why the file cannot go on, in danger tone: the parse's refusal, the
 * preview's header problems (one sentence each, through the notices module),
 * or a check that did not answer, with Try again.
 */
export const UploadBlockAlert: React.FC<UploadBlockAlertProps> = ({
    block,
    onRetry,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Alert
            status="danger"
            {...getNodeTestIdProp('catalog-import-upload-block')}
        >
            <Alert.Indicator />
            <Alert.Content>
                {block.kind === 'notices' ? (
                    <>
                        <Alert.Title>
                            {t('catalogImport.upload.blocked.notices.title')}
                        </Alert.Title>
                        {block.notices.length === 1 && block.notices[0] ? (
                            <Alert.Description>
                                {renderNotice(t, block.notices[0])}
                            </Alert.Description>
                        ) : (
                            <Alert.Description>
                                <ul className="mt-1 list-inside list-disc space-y-1">
                                    {block.notices.map((notice) => (
                                        <li key={notice.key}>
                                            {renderNotice(t, notice)}
                                        </li>
                                    ))}
                                </ul>
                            </Alert.Description>
                        )}
                    </>
                ) : (
                    <BlockCopy block={block} />
                )}
                {block.kind === 'checkFailed' ? (
                    <Button
                        className="mt-2 self-start"
                        onPress={onRetry}
                        size="sm"
                        variant="danger"
                        {...getNodeTestIdProp('catalog-import-upload-retry')}
                    >
                        {t('catalogImport.upload.file.retry')}
                    </Button>
                ) : null}
            </Alert.Content>
        </Alert>
    );
};
